// Client-side mirror of the backend's router-level plan gates. Calls to an API area
// the academy's plan doesn't include are refused locally (no 402 in the console).
// Only prefixes whose every route is gated by one feature are listed.
const PREFIX_FEATURE = {
  '/billing': 'payments.stripe_byok',
  '/stripe': 'payments.stripe_byok',
  '/blog': 'content.blog',
  '/camps': 'camps.core',
  '/classes': 'programs.classes',
  '/coach': 'programs.coaches',
  '/coach-notes': 'programs.coaches',
  '/coaches': 'programs.coaches',
  '/time-entries': 'programs.coaches',
  '/discounts': 'payments.module',
  '/invoices': 'payments.module',
  '/payments': 'payments.module',
  '/document-campaigns': 'comms.campaigns',
  '/enrollments': 'enrollments.core',
  '/movement-requests': 'enrollments.core',
  '/events': 'events.core',
  '/lanes': 'bookings.lanes',
  '/small-group': 'bookings.small_groups',
  '/staff': 'core.staff',
  '/tournaments': 'tournaments.registrations',
  '/whatsapp': 'comms.whatsapp',
};

let gate = null; // null = allow everything; otherwise Set of runtime feature keys
let settled = false;
let resolveReady;
let ready = new Promise((r) => { resolveReady = r; });

// Called by FeatureContext once /tenant/me settles (null = no gating: logged out / super admin / error).
export const setPlanGate = (runtimeFeatures) => {
  gate = Array.isArray(runtimeFeatures) ? new Set(runtimeFeatures) : null;
  settled = true;
  resolveReady();
};

// On logout / tenant switch: gated calls wait again for the next plan answer.
export const resetPlanGate = () => {
  gate = null;
  settled = false;
  ready = new Promise((r) => { resolveReady = r; });
};

const featureForUrl = (url) => {
  const path = String(url || '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '').split('?')[0];
  return PREFIX_FEATURE[`/${path.split('/')[1] || ''}`] || null;
};

// Resolves to the missing feature key (call must not go out) or null. Gated URLs wait
// (max 4s) for the plan on a cold load so nothing slips through before /tenant/me answers.
export const blockedFeatureFor = async (url, hasToken) => {
  const feature = featureForUrl(url);
  if (!feature) return null;
  if (!settled && hasToken) await Promise.race([ready, new Promise((r) => setTimeout(r, 4000))]);
  return gate && !gate.has(feature) ? feature : null;
};

// Shaped like the server's 402 so existing error handling keeps working.
export const planBlockedError = (feature, config) => {
  const err = new Error(`Feature not in plan: ${feature}`);
  err.config = config;
  err.isPlanBlocked = true;
  err.response = {
    status: 402,
    config,
    data: { detail: { code: 'feature_not_in_plan', feature_key: feature, message: "This area isn't included in your current plan." } },
  };
  return err;
};
